import { Accessor, createMemo, onMount } from "solid-js";

import { RNREvent } from "~/data/events";
import { useAnalyticsService } from "~/context/analytics_service_context";
import { BrandL2RouteData } from "~/server/data/brand_l2_route_data";
import { config } from "~/data/config";
import {
  getSimulateTransactionDetails,
  getSimulateTransactionDetails2,
} from "~/utils/brandl2";
import { isOfferAvailed } from "~/core/offer-construct/offer-construct";
import { clientIdConfig } from "~/data/partner_config";
import { useWebStoreContext } from "~/context/webstore_context";
import { FAQ } from "~/data/static_faqs/types";
import { useTataPayment } from "./use_tata_payment";

export function useTataDigitalBrandPage(brandPageProps: {
  brandKey: string;
  routeData: Accessor<BrandL2RouteData | undefined>;
}) {
  const { routeData } = brandPageProps;
  const analyticsService = useAnalyticsService();
  const { storeState, webstoreUIConfig, getClientId, getMode } =
    useWebStoreContext();

  const brand = createMemo(() => routeData()?.brand);

  const clientId = () => getClientId();

  const offerConstruct = createMemo(() => routeData()?.offerConstruct);

  const simulateTransactionDetails = createMemo(() =>
    getSimulateTransactionDetails(routeData()?.simulate)
  );
  const simulateTransactionDetails2 = createMemo(() =>
    getSimulateTransactionDetails2(routeData()?.simulate)
  );

  onMount(() => {
    analyticsService.logEvent(RNREvent.VISIT_BRANDL2_PAGE, {
      brandName: brand()?.name.toLowerCase(),
    });
  });

  // used to override the default cta copy
  function getCtaCopy() {
    if (clientId() === clientIdConfig.tdClientId) {
      return "Avail now";
    }

    if (clientId() === clientIdConfig.visitHealthRewardsClientId) {
      return "Get this card";
    }

    return "";
  }

  type UIConfig = {
    showFooter: boolean;
    showBrandingOnBottom: boolean;
    showWhatsappTip: boolean;
    showCloseButtonOnTopRight: boolean;
    showHeaderActions: boolean;
    showBreadcrumbsNav: boolean;
    showHubbleLogoOnBrandCard: boolean;
    showHubbleTrustMarker: boolean;
    showHeader: boolean;
    showFooterNotes: boolean;
  };

  function getUIConfig(): UIConfig {
    let mode = getMode();
    let isSdk = mode == "sdk";
    let isRnr = mode == "rnr";
    let isHubble = mode == "hubble";
    let showHubbleBranding = webstoreUIConfig().showHubbleBranding;
    return {
      showFooter: isHubble,
      showBrandingOnBottom: showHubbleBranding,
      showWhatsappTip:
        (isHubble || isRnr) &&
        !(getClientId() === clientIdConfig.axisClientId) &&
        !(getClientId() === clientIdConfig.rocheClientId) &&
        !(getClientId() === clientIdConfig.tdClientId) &&
        !(getClientId() === clientIdConfig.visitHealthRewardsClientId) &&
        !(getClientId() === clientIdConfig.redeemHubbleClientId) &&
        !isClientRaisoni(),
      showCloseButtonOnTopRight: isSdk,
      showHeaderActions: isHubble || isRnr,
      showBreadcrumbsNav: isHubble || isRnr,
      showHubbleLogoOnBrandCard: isHubble || showHubbleBranding,
      showHubbleTrustMarker: isHubble,
      showHeader: isHubble || isRnr,
      showFooterNotes: isHubble,
    };
  }

  function getMetaTitle(): string {
    const baseDiscountPercent =
      simulateTransactionDetails()?.discountDetails?.hubbleBaseDiscount
        ?.percentage ?? 0;

    if (baseDiscountPercent > 0) {
      if (brand()?.rewardType != "POSTPAID") {
        return `${brand()!.voucherProductMetadata.title} | Flat ${baseDiscountPercent}% Off | E-Gift Card | No Promo Code Needed`;
      } else {
        return `${brand()!.voucherProductMetadata.title} | E-Gift Card | Redeem benefit in Hubble App`;
      }
    } else
      return `${brand()!.voucherProductMetadata.title} | E-Gift Card | No Promo Code Needed`;
  }

  const isClientRaisoni = createMemo(() => {
    return clientId()?.includes(config.clientIdPrefix?.raisoni);
  });

  const offerConstructAvailed = createMemo(() => {
    return offerConstruct() && isOfferAvailed(offerConstruct()!);
  });

  function getFaqs(): FAQ[] {
    let faqs = routeData()?.brandExtraMetadata.faqs ?? [];
    return faqs;
  }

  const {
    onClickGetThisCard,
    getDenominationList,
    showFullPageLoadingOverlay,
    ctaState,
    setCtaState,
  } = useTataPayment({
    brand: () => brand()!,
    brandKey: brand()!.brandKey,
    isOfferConstructAvailable: offerConstruct() != null,
    offerConstructType: offerConstruct()?.type,
  });

  return {
    routeData,
    getUIConfig,
    getMode,
    isLoggedIn: storeState.isLoggedIn,
    offerConstructAvailed,
    getClientId,
    getMetaTitle,
    onClickGetThisCard,
    getCtaCopy,
    getDenominationList,
    getFaqs,
    showFullPageLoadingOverlay,
    ctaState,
    setCtaState,
    simulateTransactionDetails: simulateTransactionDetails(),
    simulateTransactionDetails2: simulateTransactionDetails2(),
    clientId,
    offerConstruct,
    // redemptionControls moved to denomination grid
  };
}

// animateDueAmount moved to denomination grid internal hook
